import axios, { AxiosInstance } from 'axios';
import { fetchAuthSession } from '@aws-amplify/auth';

import type { AdminAssignableTier, ManagedPlanTier } from '../constants/subscription-tiers';
import { ALLOWED_MEDIA_EXTENSIONS } from '../constants/notificationMediaTypes';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.intelligent-automation.net';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(async (config) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (error) {
    console.warn('Failed to get auth token:', error);
  }
  return config;
});

// Types
export interface User {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  organizationId?: string;
  /** Self-declared organisation / FSP name from trial signup (Trello #220). */
  organisationName?: string;
  isPaid: boolean;
  isEnabled: boolean;
  status: string;
  createdAt: string;
  lastModified?: string;
  groups: string[];
  usage?: {
    period: string;
    submittedCount: number;
    completedCount: number;
    failedCount: number;
  };
}

export interface UserDetails extends User {
  status: string;
  maxRunsPerMonth?: number;
  maxDocChatLimit?: number;
  maxResearchLimit?: number;
  embeddingsEnabled: boolean;
  documentChatEnabled: boolean;
  vectorStoreId?: string;
  mfaEnabled: boolean;
  /**
   * Static tier seat (broker-AI orgs only). Set/cleared via the user edit
   * dialog → backend `update-user` handler. Drives the org's per-cycle
   * Stitch charge (sum of active-seat priceZAR aggregator — backend MR !511).
   */
  tier?: AdminAssignableTier;
  /**
   * True when this user has NO Cognito account and the record was assembled
   * from the user-profiles row alone (backend Trello #420). Such a row still
   * appears on its organisation's Members table, so an admin can land here from
   * that table — but there is no login to disable, enable, reset or invite.
   * Only Delete is meaningful, and it is what removes the row from the list.
   * Absent on every normal user.
   */
  cognitoAccountMissing?: boolean;
}

/**
 * Response of POST /admin/users/:id/delete. Both flags default to false on the
 * backend, so an ordinary delete is unchanged; they exist to distinguish three
 * outcomes that used to be one word (backend Trello #420).
 */
export interface DeleteUserResponse {
  success: boolean;
  data?: {
    userId?: string;
    status?: string;
    /** The account was already gone; only the profile record was removed. */
    cognitoAccountMissing?: boolean;
    /**
     * The Cognito account WAS deleted but the profile row survived. Not a
     * success — this is the state that leaves an orphaned row on the org's
     * member list, and the caller must say so rather than navigating away.
     */
    profileSyncFailed?: boolean;
  };
}

export interface Job {
  jobId: string;
  userId: string;
  jobType: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  progress: number;
  currentStep?: string;
  outputLocation?: string;
  errorMessage?: string;
  processingTimeMs?: number;
  tokenUsage?: TokenUsage;
  /** Trello #202 — token-weighted credit charge for this run (base × total_tokens ÷ window), or null for a non-credit-billed tool. */
  creditsCharged?: number | null;
  /** Trello #317 — Google Vision OCR cost (USD), SEPARATE from the LLM
   *  tokenUsage.costUsd. True all-in cost = tokenUsage.costUsd + ocrCostUsd.
   *  Absent when the run never hit paid OCR (digital-PDF / pdftotext path). */
  ocrCostUsd?: number;
  /** Pages OCR'd via paid Google Vision for this run (drives ocrCostUsd). */
  ocrPages?: number;
  // Optional — populated by the org-scoped runs endpoint (a single org spans
  // many users, so the shared runs table can show who ran each job + a nicer
  // type label). The per-user endpoint omits these.
  userEmail?: string;
  jobTypeDisplayName?: string;
}

export interface Organization {
  id: string;
  name: string;
  displayName?: string;
  domain?: string;
  maxRunsPerMonth?: number;
  maxDocChatPerMonth?: number;
  maxResearchPerMonth?: number;
  features?: string[];
  enabledFeatures?: string[];
  status?: string;
  accountManagerName?: string;
  accountManagerEmail?: string;
  // Referral / distribution-partner source the org was acquired through, e.g.
  // "FSP Hub" vs "Direct". Internal acquisition tracking (Trello #260).
  referralSource?: string;
  // Org pays by direct EFT into the bank account (admin-set toggle). Counts
  // the org as paying for revenue + FSP Hub commission reporting (Trello #299);
  // its revenue is monthlyAmountZAR. Broker-AI concern only.
  eftPayer?: boolean;
  // Organisation owner — a pointer to ONE of the org's own users (distinct from
  // accountManager*, our salesperson). The owner is who an admin-generated Stitch
  // payment link bills; name/email are denormalised for display + the payer.
  ownerUserId?: string;
  ownerName?: string;
  ownerEmail?: string;
  // Per-org payment mode. 'self-serve' (default) sees the in-app payment page;
  // 'managed' is comp'd (paid by us) — no payment page, pool activated by us.
  billingMode?: 'self-serve' | 'managed';
  // Live self-serve payment state, derived backend-side by joining the
  // org-subscriptions table. Only returned on the broker-AI admin build (the
  // sole self-serve tenant); undefined on the IA build (and on an older backend
  // that predates the join — consumers fall back to the billingMode display).
  // Drives the Organizations "Billing" column so the broker team can see which
  // self-serve orgs are actually paying. (Marcus 2026-06-29)
  subscriptionTag?: OrgSubscriptionTag;
  // Manually-set billing tag for the sales-led Intelligent Automation tenant,
  // which has no self-serve subscription to auto-derive from (unlike broker-AI's
  // subscriptionTag above). Admin-set on the org detail page; drives the IA admin
  // Organizations "Billing" column + filter (blank until set). Absent on broker-AI.
  manualBillingStatus?: 'trial' | 'unpaid' | 'paid';
  // Active managed (comp'd) plan tier — lets the managed-plan dropdown restore
  // its saved selection after a refresh instead of defaulting to Bronze
  // (Trello #280). Absent for self-serve orgs.
  managedTier?: ManagedPlanTier;
  billingName?: string;
  billingAddress?: string;
  vatNumber?: string;
  costPerLicenseZAR?: number;
  // Subscription billing (set in Edit Billing Information). The invoice
  // auto-pulls these. monthlyAmountZAR is ZAR rands (decimal), not cents.
  // Required on BOTH stacks — the backend's InvoiceBillingDataError names them
  // for an IA org too (Trello #670), it was only the UI that treated them as
  // broker-AI-only.
  tierName?: string;
  monthlyAmountZAR?: number;
  /**
   * The description line printed on this org's invoices, replacing the brand
   * default ("Intelligent Automation Subscription — August 2026"). Absent or
   * empty = the brand default. Feeds BOTH the admin's manual generate and the
   * automated EFT pro-forma, so the two cannot print different wording for the
   * same customer (Trello #670).
   */
  invoiceDescription?: string;
  // Live self-serve subscription's recurring price (ZAR rands), joined
  // backend-side from the org-subscriptions record (priceZAR cents ÷ 100). The
  // AUTHORITATIVE monthly revenue for a 'paid' org — unlike monthlyAmountZAR it
  // needs no manual entry. Only returned on the broker-AI build when a
  // subscription row exists; undefined otherwise. (Trello #299)
  subscriptionMonthlyZAR?: number;
  // Product stack: 'broker-ai' orgs get the new PDF invoice; others keep DOCX.
  stack?: string;
  sharedRunPool?: boolean;
  mfaRequired?: boolean;
  branding?: {
    displayName?: string;
    logoUrl?: string;
    // Org-level document font for generated DOCX outputs. Validated server-side
    // against SUPPORTED_DOCUMENT_FONTS; empty string clears the override and
    // reverts to the default font.
    font?: string;
    // Server-derived preview metadata. Populated by get-organization when a
    // logo file actually exists at <orgId>/branding/document-header.png in
    // the templates bucket. Absent when no logo is uploaded.
    logoDownloadUrl?: string;
    logoSizeBytes?: number;
    logoLastModified?: string;
    // Same as the three fields above, but for the outbound-email header logo
    // at <orgId>/branding/email-header.png. Distinct S3 key per the 2026-04-20
    // schema split — see legal-ai-backend src/utils/email/email-sender.ts.
    emailLogoDownloadUrl?: string;
    emailLogoSizeBytes?: number;
    emailLogoLastModified?: string;
    // Per-org document colour scheme, stored as BARE 6-hex (`008C9B`, no `#`)
    // because that is the form the `docx` library's `color` field wants. The
    // pickers below work in `#RRGGBB`; convert on both edges. Absent or
    // partially-set is normal — each missing role falls back to the template's
    // own default server-side (see resolveOrgColorScheme in legal-ai-backend).
    documentColors?: {
      /** Main headings. Was "headings AND body text" until Trello #645. */
      heading?: string;
      /** Section sub-headings. */
      subheading?: string;
      /** Body copy. Falls back to `heading` server-side while unset. */
      body?: string;
      /** Important highlights / calls-to-action. */
      highlight?: string;
    };
    // Document font ROLES (Trello #645). `font` above is the legacy single
    // face and is still what the backend reads for `body` when `fonts.body`
    // is absent — which is every org that has not touched the new pickers.
    fonts?: {
      heading?: string;
      body?: string;
    };
    /**
     * Document LETTERHEAD (Trello #684) — the standing footer block generated
     * documents carry on every page, and whether the header banner repeats past
     * page one. Absent for every org that has not configured one, which is the
     * normal case and renders exactly as it always did.
     */
    letterhead?: {
      footerLines?: string[];
      repeatHeaderOnEveryPage?: boolean;
    };
  };
}

/**
 * Admin billing + package + credit summary for one org (Trello #319). Returned
 * as `data.billing` from GET /admin/organizations/:orgId so the detail page can
 * show what the org bought + how its credit balance is made up without
 * cross-referencing Stitch. Mirrors the backend `OrgBillingSummary`. Absent (or
 * undefined fields) when the backend hasn't been deployed yet — the detail page
 * degrades gracefully.
 */
export interface OrgBillingSummary {
  /** Coarse billing status — same classifier as the Organizations list column. */
  billingStatus: 'managed' | 'paid' | 'trial' | 'unpaid' | 'none';
  /** Raw subscription lifecycle status; null when the org has no subscription. */
  subscriptionStatus: string | null;
  managed: boolean;
  eftPayer: boolean;
  /** Display package: Bronze / Silver / Gold / Enterprise / Trial / Standard / '—'. */
  packageLabel: string;
  tier: string | null;
  /** Monthly recurring price in ZAR rands; null when no subscription. */
  priceZAR: number | null;
  creditModel: 'org-tier-pool' | 'shared-pool' | 'per-seat' | 'none';
  standardMonthlyCredits: number | null;
  adjustedMonthlyCredits: number | null;
  monthlyCreditsAllocation: number | null;
  monthlyCreditsUsed: number | null;
  monthlyCreditsRemaining: number | null;
  topUpCreditsRemaining: number | null;
  totalCreditsRemaining: number | null;
  subscriptionStartDate: string | null;
  nextBillingDate: string | null;
  /**
   * When the subscription was cancelled; null while it is running. Set WITH
   * `subscriptionStatus: 'active'` for a cancel-at-period-end that has not lapsed
   * yet — the org keeps access until `currentPeriodEnd`. Optional: absent against
   * a backend that predates the field (deploy ordering: backend first).
   */
  canceledAt?: string | null;
  /**
   * End of the period the org has already paid for — when access actually lapses
   * after a cancel-at-period-end. Distinct from `nextBillingDate`, which is the
   * next CHARGE and is meaningless once the Stitch subscription is revoked.
   */
  currentPeriodEnd?: string | null;
}

export interface OrganizationInvoice {
  period: string;
  invoiceNumber: string;
  exists: boolean;
  url: string | null;
  generatedAt: string | null;
  fileSize: number | null;
}

/**
 * Bulk-invoice run modes (Trello #363).
 *  - preview:  resolve what each org WOULD be invoiced. Commits nothing.
 *  - generate: build + store each invoice, then bundle them into a ZIP.
 *  - export:   bundle invoices that already exist; generate nothing.
 */
export type BulkInvoiceMode = 'preview' | 'generate' | 'export';

export type BulkInvoiceRowStatus =
  | 'ready'
  | 'generated'
  | 'exported'
  | 'missing'
  | 'skipped'
  | 'failed'
  | 'not-attempted';

/**
 * Status of an async admin-export job, as returned by `getExportJobStatus`.
 * Shared here (next to the method) so a consumer doesn't re-declare it; the
 * bulk-invoice fields are only populated for `exportType: 'bulk-invoices'`.
 */
export interface AdminExportJob {
  jobId: string;
  status: 'QUEUED' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  progress: number;
  currentStep: string;
  downloadUrl?: string;
  errorMessage?: string;
  metadata?: {
    exportType?: string;
    period?: string;
    organizationId?: string;
    /** The run hit its time budget — some months were not processed. */
    truncated?: boolean;
    bulkInvoiceMode?: BulkInvoiceMode;
    bulkInvoiceCounts?: Record<BulkInvoiceRowStatus, number>;
    bulkInvoiceTotalZar?: number;
    bulkInvoiceRows?: BulkInvoiceRow[];
    /** Set when the grid was too large to store on the job — the file has it all. */
    bulkInvoiceRowsOmitted?: boolean;
  };
}

/** One (organisation x month) unit of a bulk-invoice run. */
export interface BulkInvoiceRow {
  organizationId: string;
  organizationName: string;
  period: string;
  status: BulkInvoiceRowStatus;
  invoiceNumber?: string;
  amountZar?: number;
  tierName?: string;
  note?: string;
}

export interface NdaRecord {
  filename: string;
  recipientName: string;
  generatedAt: string;
  fileSize: number;
  url: string;
}

export interface AgreementRecord {
  filename: string;
  customerName: string;
  generatedAt: string;
  fileSize: number;
  url: string;
}

export interface RecentJob {
  jobId: string;
  userId: string;
  userEmail?: string;
  jobType: string;
  jobTypeDisplayName: string;
  status: string;
  createdAt: string;
  /** Broker Doc Assist chat id — shared by every turn of one chat (upload +
   *  follow-up questions). Undefined for every other run type. Drives the
   *  Recent Activity table's grouping of a chat's turns. */
  conversationId?: string;
  /** Trello #317 — true all-in cost = LLM token cost + Google Vision OCR cost.
   *  The split is in llmCostUsd / ocrCostUsd. */
  costUsd: number;
  /** LLM token-call portion of costUsd (USD). */
  llmCostUsd?: number;
  /** Google Vision OCR portion of costUsd (USD). 0 for digital-PDF jobs. */
  ocrCostUsd?: number;
  /** Pages OCR'd via paid Google Vision (drives ocrCostUsd). */
  ocrPages?: number;
  processingTimeMs?: number;
  errorMessage?: string;
  inputTokens?: number;
  outputTokens?: number;
  /** Trello #202 — token-weighted credit charge for this run (base × total_tokens ÷ window), or null for a non-credit-billed tool. */
  creditsCharged?: number | null;
  /** Raw char count of Anthropic extended-thinking blocks. Anthropic bundles
   * thinking tokens into output_tokens; chars are the actual signal. */
  thinkingChars?: number;
  outputLocation?: string;
  /** User-submitted quality rating (1–5), undefined if unrated */
  rating?: number;
  ratedAt?: string;
  /** ISO timestamp set when an admin clicked "Notify customer" on a failed
   * row. Drives the disabled "Notified <date>" state — one-shot per job. */
  failureNotifiedAt?: string;
  /** ISO timestamp set when an admin clicked "Mark resolved" (only available
   * after notifying). Drives the disabled "Resolved <date>" state — one-shot. */
  failureResolvedAt?: string;
}

/**
 * A grouped row for the Recent Activity table. A Broker Doc Assist chat writes
 * one job per turn (the upload that opens it + each follow-up question), all
 * sharing a conversationId; grouping collapses them into ONE collapsible parent
 * so a chat doesn't fill the table with N near-identical rows. Every other run
 * (and a single-turn chat) is a singleton group. Mirrors the backend
 * DashboardRunGroup (get-overview-stats). */
export interface RecentRunGroup {
  /** conversationId for a chat, jobId for a singleton. Stable expand-key. */
  groupId: string;
  /** True only for a multi-turn Broker Doc Assist chat. */
  isConversation: boolean;
  /** Row shown collapsed — a synthetic aggregate for a chat, the run itself for a singleton. */
  parent: RecentJob;
  /** Every turn, oldest-first (opener first). For a singleton: [parent]. */
  turns: RecentJob[];
  /** turns.length — drives the "N messages" badge. */
  turnCount: number;
}

export type ProviderHealthStatus = 'healthy' | 'degraded' | 'unhealthy';

export interface ProviderHealthRecord {
  providerId: string;
  status: ProviderHealthStatus;
  lastCheckedAt: string;
  latencyMs: number | null;
  errorMessage: string | null;
  errorStatusCode: number | null;
}

export interface OverviewStats {
  period: string;
  generatedAt: string;
  totalUsers: number;
  totalOrganizations: number;
  jobsToday: number;
  jobsByStatus: Record<string, number>;
  /**
   * Top 10 slowest COMPLETED runs over the last 30 days, ordered by
   * processingTimeMs desc. Drives the dashboard's "Slowest Runs" chart.
   */
  slowestRunsLast30Days: Array<{
    jobId: string;
    jobType: string;
    displayName: string;
    processingTimeMs: number;
    createdAt: string;
    userEmail: string;
  }>;
  activeProcessing: number;
  totalCostToday?: number;
  dailyCosts?: Array<{ date: string; cost: number }>;
  dailyCompletedJobs?: Array<{ date: string; count: number }>;
  /**
   * Mean processing time (ms) per day over the last 30 days. Drives the
   * "Average Processing Time" trend chart. sampleSize is included so the
   * tooltip can show how many jobs contributed to a given day's mean.
   */
  dailyAverageProcessingTime?: Array<{ date: string; averageMs: number; sampleSize: number }>;
  recentJobs?: RecentJob[];
  totalRecentJobs?: number; // Total count for pagination
  /** Recent runs with Doc Assist chats collapsed per conversation (grouped
   *  BEFORE pagination). The table renders from this when present; recentJobs
   *  is the flat fallback for an older backend during the deploy window. */
  recentRunGroups?: RecentRunGroup[];
  /** Total group count for pagination (conversations + singleton runs). */
  totalRecentRunGroups?: number;
  /** Mean of dailyCosts over days that had cost. Null when no completed jobs in the window. */
  cost30dAvg?: number | null;
  /** Latest synthetic-ping result. Null when no ping has run yet. */
  providerHealth?: ProviderHealthRecord | null;
  /**
   * Latest synthetic-ping result for Google Cloud Vision (the OCR provider).
   * Optional because the backend field ships in its own MR — an older backend
   * simply omits it and the tile renders "Unknown" rather than breaking.
   */
  gcvHealth?: ProviderHealthRecord | null;
}

// AWS resource health snapshot for the Resources tab.
// Mirrors backend src/handlers/admin/get-aws-health.ts response shape.
export type AwsHealthWindow = '1h' | '24h' | '7d' | '30d';

export interface AwsHealthSparkPoint {
  ts: string;
  value: number;
}

export interface AwsHealth {
  generatedAt: string;
  region: string;
  /** Selected aggregation window — applied to totals + sparkline (NOT to SQS / DLQ / SES, which are point-in-time / per-day). */
  window: AwsHealthWindow;
  cached: boolean;
  cacheAgeMs: number;
  lambda: {
    concurrencyLimit: number | null;
    /** Peak ConcurrentExecutions over the selected window. */
    concurrencyMaxInWindow: number | null;
    concurrencySpark: AwsHealthSparkPoint[];
    /** Total in selected window. */
    throttles: number | null;
    /** Total in selected window. */
    errors: number | null;
    /** Peak memory utilization on legal-ai-api over the window, as a percentage
     *  of allocated memory_size. Derived from `Max Memory Used: N MB` REPORT
     *  log lines via CloudWatch Logs Insights — capacity-planning signal:
     *  sustained values >80% mean it's time to bump lambda_memory_size. */
    memoryUtilizationMaxPct: number | null;
    /** Memory utilization % over the window for trend visibility. */
    memoryUtilizationSpark: AwsHealthSparkPoint[];
  };
  sqs: {
    queueCount: number;
    topByAge: Array<{ name: string; ageSec: number; depth: number }>;
    dlqDepthTotal: number;
    dlqWithMessages: Array<{ name: string; depth: number }>;
  };
  dynamodb: {
    /** Total in selected window. */
    readThrottles: number | null;
    /** Total in selected window. */
    writeThrottles: number | null;
  };
  apiGateway: {
    requests: number | null;
    error5xx: number | null;
    error4xx: number | null;
    error5xxRate: number | null;
    p99LatencyMs: number | null;
  };
  anthropic: {
    throttles: number | null;
    throttlesSpark: AwsHealthSparkPoint[];
  };
  ses: {
    dailyQuota: number | null;
    /** Always last 24h vs daily quota — the quota is per-day so window doesn't apply. */
    sentLast24h: number | null;
  };
  /** Aurora pgvector — RAG / embeddings DB. */
  aurora: {
    enabled: boolean;
    acuConsumed: number | null;
    /** Peak DatabaseConnections over the selected window. */
    connectionsMaxInWindow: number | null;
    connectionsSpark: AwsHealthSparkPoint[];
    cpuMaxPct: number | null;
    replicaLagMaxMs: number | null;
  };
  /** Cognito per-pool throttles + sign-ins. */
  cognito: {
    pools: Array<{
      label: 'main' | 'broker';
      poolId: string | null;
      throttles: number | null;
      signInSuccesses: number | null;
      tokenRefreshSuccesses: number | null;
    }>;
  };
  /** Top Lambda functions by p99 duration over the selected window. */
  lambdaFunctions: Array<{
    name: string;
    p99DurationMs: number | null;
    invocations: number | null;
    errors: number | null;
  }>;
  /** API Gateway 4xx sparkline (includes 429 throttles — separate quota from 5xx). */
  apiGateway4xxSpark: AwsHealthSparkPoint[];
  topOrgs: Array<{ organizationId: string; jobCount: number }>;
  topUsers: Array<{ userId: string; userEmail: string | null; jobCount: number }>;
  errors: {
    cloudwatch: boolean;
    serviceQuotas: boolean;
    sqs: boolean;
    /** True if the DDB scan for top orgs/users failed. */
    dynamodb: boolean;
  };
}

export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  model?: string;
  costUsd?: number;
  // Raw character count of Anthropic extended-thinking blocks.
  // Anthropic bundles thinking tokens into output_tokens; chars are the actual signal.
  thinkingChars?: number;
}

// Per-stack cost report
export type AdminStackKey = 'intelligent-automation' | 'broker-ai';

export interface CostReportStackTotals {
  totalCostUsd: number;
  submitted: number;
  completed: number;
  failed: number;
}

export interface CostReportOrgRow {
  organizationId: string;
  organizationName?: string;
  stack: AdminStackKey;
  totalCostUsd: number;
  submitted: number;
  completed: number;
  failed: number;
}

export interface CostReport {
  period: string;
  generatedAt: string;
  adminStack: AdminStackKey;
  byStack: Record<AdminStackKey, CostReportStackTotals>;
  titanShareUsd: number;
  byOrg: CostReportOrgRow[];
  eligibleOrgsForStack: number;
  scannedRows: number;
}

// Broker-AI profitability report (server-computed margins + rolling trend).
export interface ProfitabilityTier {
  name: string;
  proposed: boolean;
  chargeZar: number;
  fspChargeZar: number;
  costZar: number | null;
  profitZar: number | null;
  marginPct: number | null;
  fspProfitZar: number | null;
  fspMarginPct: number | null;
}

export interface ProfitabilityTrendPoint {
  period: string;
  label: string;
  completed: number;
  avgCostZar: number;
  marginByTier: Record<string, number>;
}

export interface ProfitabilityReport {
  available: boolean;
  reason?: string;
  period?: string;
  generatedAt?: string;
  adminStack?: AdminStackKey;
  usdZarRate?: number | null;
  fxSource?: string;
  fxFetchedAt?: string | null;
  fspDiscountPct?: number;
  current?: {
    completed: number;
    totalCostUsd: number;
    avgCostUsd: number;
    avgCostZar: number | null;
  };
  tiers?: ProfitabilityTier[];
  trend?: ProfitabilityTrendPoint[];
  trendTierNames?: string[];
}

// Per-org run-usage health (Usage Health dashboard)
export type UsageHealthStatus = 'healthy' | 'low' | 'critical' | 'alert' | 'unknown';
export type UsagePoolModel = 'shared' | 'per-seat' | 'unconfigured';

/**
 * Live org paying status (joined backend-side from the org-subscriptions table).
 * 'paid'/'managed' = a paying org; 'trial'/'unpaid' = not paying. Shared by the
 * Organizations "Billing" column and the Usage Health paying filter (Trello #335).
 */
export type OrgSubscriptionTag = 'managed' | 'paid' | 'trial' | 'unpaid';

export interface OrgUsageHealthRow {
  organizationId: string;
  organizationName?: string;
  stack: AdminStackKey;
  poolModel: UsagePoolModel;
  tierName?: string;
  seatCount?: number;
  runsAllocated: number | null;
  runsUsed: number;
  runsRemaining: number | null;
  percentConsumed: number | null;
  status: UsageHealthStatus;
  // Live paying status (Trello #335). undefined when the backend join failed or
  // predates the field — the row then renders billing as "—" (unknown).
  subscriptionTag?: OrgSubscriptionTag;
  // The org's OWN billing signals, so the Billing column + "Paying only" filter
  // derive the SAME effective tag the Organizations table shows (Trello #377).
  // An EFT payer has no Stitch subscription, so its subscriptionTag joins to
  // 'unpaid' — without eftPayer, Usage Health labelled real paying customers
  // "Unpaid" and the paying filter silently dropped them. All undefined against
  // a backend predating the passthrough; the derivation then falls back to
  // subscriptionTag alone, i.e. exactly the previous behaviour.
  eftPayer?: boolean;
  billingMode?: 'self-serve' | 'managed';
  // Carried deliberately even though it is UNREACHABLE today: the Billing UI is
  // gated on `IS_BROKER_ADMIN`, and `effectiveBillingTag` only reads this field
  // on the IA build. It is here so a usage-health row and an Organizations row
  // are interchangeable inputs to the one shared derivation — otherwise enabling
  // Usage Health billing on IA later would silently render a blank column, the
  // quiet failure this consolidation exists to prevent. Optional, so it costs
  // nothing at runtime.
  manualBillingStatus?: 'trial' | 'unpaid' | 'paid';
}

export interface UsageHealthSummary {
  totalOrgs: number;
  healthy: number;
  low: number;
  critical: number;
  alert: number;
  unknown: number;
}

export interface UsageHealthReport {
  period: string;
  generatedAt: string;
  adminStack: AdminStackKey;
  orgs: OrgUsageHealthRow[];
  summary: UsageHealthSummary;
}

// Per-USER run usage (Trello #246 part 2) — surfaces the "champions" inside
// brokerages. Reuses the same status + pool vocabulary as the per-org report.
export interface PerUserUsageRow {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  organizationId?: string;
  organizationName?: string;
  tierName?: string;
  poolModel: UsagePoolModel;
  runsUsed: number;
  /** Per-seat user's seat quota; null for shared-pool (no personal cap) / unconfigured. */
  runsAllocated: number | null;
  runsRemaining: number | null;
  percentConsumed: number | null;
  status: UsageHealthStatus;
}

export interface PerUserUsageSummary {
  totalUsers: number;
  activeUsers: number;
  totalRuns: number;
  averageRunsPerUser: number;
  topUserEmail?: string;
}

export interface PerUserUsageReport {
  period: string;
  generatedAt: string;
  adminStack: AdminStackKey;
  users: PerUserUsageRow[];
  summary: PerUserUsageSummary;
  /** Trello #246 pagination: rows are capped to top-N; total = pre-cap count. */
  total?: number;
  truncated?: boolean;
  limit?: number;
}

// Most-used tools (Trello #246 part 3) — which tools brokers actually run, for
// the admin's stack + selected month. Each run counted once; stack-scoped via
// the Cognito pool. Denominated in runs (~200k tokens each).
export interface ToolUsageRow {
  jobType: string;
  displayName: string;
  runs: number;
  /** 0–100 percent of the stack's total runs this period (1 d.p.). */
  percentOfTotal: number;
}

export interface ToolUsageSummary {
  totalRuns: number;
  distinctTools: number;
  topToolDisplayName?: string;
}

export interface MostUsedToolsReport {
  period: string;
  generatedAt: string;
  adminStack: AdminStackKey;
  tools: ToolUsageRow[];
  summary: ToolUsageSummary;
}

// Broker AI (Titan) adoption — how many orgs have the Titan models enabled,
// across BOTH stacks (intentionally cross-stack). GET /admin/stats/broker-adoption.
export interface BrokerAdoptionStackSplit {
  totalOrgs: number;
  withBrokerAI: number;
}
export interface BrokerAdoptionSummary {
  totalOrgs: number;
  orgsWithBrokerAI: number;
  /** orgsWithBrokerAI / totalOrgs as a whole-number percent (0-100). */
  adoptionPercent: number;
  byStack: Record<AdminStackKey, BrokerAdoptionStackSplit>;
}
export interface BrokerModelAdoption {
  featureId: string;
  name: string;
  orgsEnabled: number;
}
// Per-org commercial snapshot (Trello #320) — payment method, package, amount,
// billing status, when-they-started-paying, creation date. `billing` is optional
// so the FE renders safely against a backend that predates the field (deploy
// ordering: backend first).
export interface BrokerAdoptionBilling {
  /** 'Subscription' | 'EFT' | 'Managed' | 'Trial' | '-'. */
  paymentMethod: string;
  /** 'paid' | 'trial' | 'unpaid' | 'managed' | 'none'. */
  billingStatus: 'paid' | 'trial' | 'unpaid' | 'managed' | 'none';
  /** Bronze / Silver / Gold / Enterprise / Trial / '-'. */
  packageLabel: string;
  /** Monthly recurring amount in ZAR rands; null when unknown. */
  monthlyAmountZAR: number | null;
  /** ISO timestamp the org started paying; null when unknown. */
  startedPayingAt: string | null;
  /** ISO timestamp the org was created; null for legacy rows. */
  createdAt: string | null;
}
// One Titan model's run count for an org this period (Trello #320) — the accordion breakdown.
export interface BrokerAdoptionModelRun {
  featureId: string;
  name: string;
  runs: number;
}
export interface BrokerAdoptionOrgRow {
  organizationId: string;
  name: string;
  stack: AdminStackKey;
  status: string;
  enabledModels: string[];
  enabledModelCount: number;
  /** BROKER-AI (Titan) model runs for the period (Trello #320) — Titan-only, not the all-tool total. */
  runs: number;
  /** Per-Titan-model run breakdown for the row's expand/accordion; optional for old-backend deploys. */
  modelRuns?: BrokerAdoptionModelRun[];
  /** Commercial snapshot (Trello #320); optional for backend-before-frontend deploys. */
  billing?: BrokerAdoptionBilling;
}
export interface BrokerAdoptionReport {
  period: string;
  generatedAt: string;
  summary: BrokerAdoptionSummary;
  models: BrokerModelAdoption[];
  orgs: BrokerAdoptionOrgRow[];
}

// Inactive users (Trello #246 part 6) — users bucketed by days since their last
// run (churn risk). Reads the F2 lastRunAt denormalization; "now"-relative, no
// period. lastLoginAt is forward-wired; reads "not tracked yet" until F1 ships.
export interface InactiveUserRow {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  organizationId?: string;
  /** Organisation display name resolved server-side (Trello #607). */
  organizationName?: string;
  lastRunAt: string | null;
  /** Whole days since last run; null = never run. */
  daysSinceLastRun: number | null;
  neverRun: boolean;
  lastLoginAt: string | null;
  daysSinceLastLogin: number | null;
}

export interface InactiveUsersSummary {
  totalUsers: number;
  neverRun: number;
  inactive7: number;
  inactive14: number;
  inactive30: number;
  /** True once login tracking (F1) is live + data has accrued. */
  loginTrackingActive: boolean;
}

export interface InactiveUsersReport {
  generatedAt: string;
  adminStack: AdminStackKey;
  thresholds: number[];
  users: InactiveUserRow[];
  summary: InactiveUsersSummary;
  /** Trello #246 pagination: rows are capped to top-N; total = pre-cap count. */
  total?: number;
  truncated?: boolean;
  limit?: number;
  /** The search term the server applied, or null when browsing (Trello #607). */
  search?: string | null;
}

export type InactiveUsersExportFormat = 'xlsx' | 'pdf';

/**
 * The rendered document the same route returns for `?format=` (Trello #663).
 *
 * Every field is optional on purpose. The admin app and the backend deploy
 * separately, so a build of this page can reach an environment whose Lambda
 * predates #663 — there the `format` param is simply an unknown query string,
 * the JSON report comes back with `success: true`, and `fileBase64` is absent.
 * Typing it as optional forces the caller to check rather than handing a
 * viewer an empty download.
 */
export interface InactiveUsersExport {
  filename?: string;
  contentType?: string;
  fileBase64?: string;
  /** Rows actually in the file — NOT capped, unlike `InactiveUsersReport.users`. */
  exportedRows?: number;
  threshold?: number;
  search?: string | null;
  summary?: InactiveUsersSummary;
  generatedAt?: string;
}

// Activity leaderboard (Trello #246 part 4) — most-active users + orgs by runs
// for the selected month. Each run counted once; stack-scoped via the pool.
export interface UserLeaderboardRow {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  organizationId?: string;
  organizationName?: string;
  runs: number;
  lastRunAt: string | null;
  daysSinceLastRun: number | null;
}

export interface OrgLeaderboardRow {
  organizationId: string;
  organizationName?: string;
  runs: number;
  memberCount: number;
  activeMembers: number;
}

export interface ActivityLeaderboardSummary {
  totalRuns: number;
  activeUsers: number;
  totalUsers: number;
  activeOrgs: number;
  topUserEmail?: string;
  topOrgName?: string;
}

export interface ActivityLeaderboardReport {
  /** 'range' = date-range mode (#514); 'period' = legacy monthly mode. Older backends omit it. */
  mode?: 'range' | 'period';
  /** Present in monthly (period) mode only. */
  period?: string;
  /** Present in date-range mode (#514) — the resolved UTC ISO window echoed back. */
  from?: string;
  to?: string;
  /** Echoed org filter when the report was scoped to one company (#514). */
  organizationId?: string;
  generatedAt: string;
  adminStack: AdminStackKey;
  topUsers: UserLeaderboardRow[];
  topOrgs: OrgLeaderboardRow[];
  summary: ActivityLeaderboardSummary;
  /** Dimensions deferred from v1 (documents-generated). */
  deferred: string[];
  /** Trello #246 pagination: lists capped to top-N; totals = pre-cap counts. */
  totalUsers?: number;
  totalOrgs?: number;
  truncated?: boolean;
  limit?: number;
}

// Trello #246 #5 — Trial & Conversion Funnel.
export type FunnelStageKey = 'registered' | 'loggedIn' | 'ranAJob' | 'inTrial' | 'converted';
export interface FunnelStage {
  key: FunnelStageKey;
  label: string;
  count: number;
  /** % of the immediately-previous stage retained (null for `registered`). */
  ofPreviousPct: number | null;
  /** % of `registered` retained. */
  ofRegisteredPct: number;
}
export interface ConversionFunnelReport {
  generatedAt: string;
  adminStack: AdminStackKey;
  stages: FunnelStage[];
  /** Churn-risk side metric: users in an expired trial. */
  trialExpired: number;
  /** False until F1 login data accrues (no historical backfill). */
  loginTrackingActive: boolean;
  deferred: string[];
}

// Trello #204 follow-up — end-of-trial reviews surfaced on the admin Feedback
// page (rating + the two written answers). Mirrors the backend get-trial-feedback
// response shape.
export interface TrialFeedbackRow {
  id: string;
  createdAt: string;
  userId?: string;
  email?: string;
  organizationId?: string;
  rating?: number;
  mostUseful?: string;
  suggestions?: string;
}
export interface TrialFeedbackSummary {
  total: number;
  /** Mean rating to 1dp across rated reviews; null when none are rated. */
  averageRating: number | null;
  withComments: number;
}
export interface TrialFeedbackReport {
  adminStack: AdminStackKey;
  generatedAt: string;
  items: TrialFeedbackRow[];
  count: number;
  truncated: boolean;
  limit: number;
  summary: TrialFeedbackSummary;
}

// Trello #371 — the in-app "Feedback & Ideas" queue. Distinct from the
// trial reviews above: these are user-submitted suggestions / tool ideas / bug
// reports that the team triages through a status pipeline. Mirrors the backend
// list-platform-feedback response shape.
export const PLATFORM_FEEDBACK_TYPES = [
  'improvement',
  'new_tool_idea',
  'issue_bug',
  'general',
] as const;
export type PlatformFeedbackType = (typeof PLATFORM_FEEDBACK_TYPES)[number];

export const PLATFORM_FEEDBACK_TYPE_LABELS: Record<PlatformFeedbackType, string> = {
  improvement: 'Improvement',
  new_tool_idea: 'New Tool Idea',
  issue_bug: 'Issue / Bug',
  general: 'General Feedback',
};

// Ordered as the team moves an item through the pipeline — the dropdown renders
// in this order, so it doubles as the workflow definition.
export const PLATFORM_FEEDBACK_STATUSES = [
  'new',
  'under_review',
  'planned',
  'in_development',
  'completed',
  'not_proceeding',
] as const;
export type PlatformFeedbackStatus = (typeof PLATFORM_FEEDBACK_STATUSES)[number];

export const PLATFORM_FEEDBACK_STATUS_LABELS: Record<PlatformFeedbackStatus, string> = {
  new: 'New',
  under_review: 'Under Review',
  planned: 'Planned',
  in_development: 'In Development',
  completed: 'Completed',
  not_proceeding: 'Not Proceeding',
};

export interface PlatformFeedbackRow {
  id: string;
  type: PlatformFeedbackType;
  status: PlatformFeedbackStatus;
  title: string;
  description: string;
  userId: string;
  email?: string;
  organizationId?: string;
  createdAt: string;
  updatedAt: string;
  statusUpdatedAt?: string;
  statusUpdatedBy?: string;
  adminNote?: string;
}
export interface PlatformFeedbackSummary {
  total: number;
  /** Still untriaged (`new`) — the count that needs action. */
  open: number;
  byType: Record<PlatformFeedbackType, number>;
  byStatus: Record<PlatformFeedbackStatus, number>;
}
export interface PlatformFeedbackReport {
  adminStack: AdminStackKey;
  generatedAt: string;
  items: PlatformFeedbackRow[];
  count: number;
  truncated: boolean;
  limit: number;
  summary: PlatformFeedbackSummary;
}

// Feature types
export enum Features {
  MEMO = 'MEMO',
  MERITS_ASSESSMENT = 'MERITS_ASSESSMENT',
  QUANTUM = 'QUANTUM',
  LOSS_ADJUSTER_REPORT = 'LOSS_ADJUSTER_REPORT',
  RECOVERY_MODEL = 'RECOVERY_MODEL',
  JUDGEMENT_SIMULATION = 'JUDGEMENT_SIMULATION',
  OMBUDSMAN_SIMULATION = 'OMBUDSMAN_SIMULATION',
  THIRD_PARTY_RESPONSE = 'THIRD_PARTY_RESPONSE',
  POLICY_RESPONSE = 'POLICY_RESPONSE',
  POLICY_ANALYSIS = 'POLICY_ANALYSIS',
  POLICY_WORDING_ANALYSIS = 'POLICY_WORDING_ANALYSIS',
  POLICY_WORDING_COMPARISON_COMMERCIAL = 'POLICY_WORDING_COMPARISON_COMMERCIAL',
  POLICY_WORDING_COMPARISON_PERSONAL = 'POLICY_WORDING_COMPARISON_PERSONAL',
  POLICY_CHANGE_COMPLIANCE_CHECKER = 'POLICY_CHANGE_COMPLIANCE_CHECKER',
  POLICY_QUOTE_COMPARISON = 'POLICY_QUOTE_COMPARISON',
  POLICY_QUOTE_COMPARISON_V2 = 'POLICY_QUOTE_COMPARISON_V2',
  POLICY_QUOTE_COMPARISON_V3 = 'POLICY_QUOTE_COMPARISON_V3',
  TITAN_LIFE_QUOTE_COMPARISON = 'TITAN_LIFE_QUOTE_COMPARISON',
  EXPLANATION = 'EXPLANATION',
  EXPLANATION_COMPREHENSIVE = 'EXPLANATION_COMPREHENSIVE',
  EXPLANATION_COMPREHENSIVE_V2 = 'EXPLANATION_COMPREHENSIVE_V2',
  HPCSA_PI_RESPONSE = 'HPCSA_PI_RESPONSE',
  BROKER_RISK_ASSESSMENT = 'BROKER_RISK_ASSESSMENT',
  REPUDIATION_ASSESSMENT = 'REPUDIATION_ASSESSMENT',
  RECORD_OF_ADVICE_PERSONAL_STI = 'RECORD_OF_ADVICE_PERSONAL_STI',
  PERSONAL_ROA = 'PERSONAL_ROA',
  CONFIRMATION_OF_COVER = 'CONFIRMATION_OF_COVER',
  CLIENT_MINUTES_BROKER = 'CLIENT_MINUTES_BROKER',
  RECORD_OF_ADVICE_PERSONAL_STI_REPLACE = 'RECORD_OF_ADVICE_PERSONAL_STI_REPLACE',
  RECORD_OF_ADVICE_PERSONAL_RENEWAL = 'RECORD_OF_ADVICE_PERSONAL_RENEWAL',
  RECORD_OF_ADVICE_COMMERCIAL = 'RECORD_OF_ADVICE_COMMERCIAL',
  RECORD_OF_ADVICE_COMMERCIAL_V2 = 'RECORD_OF_ADVICE_COMMERCIAL_V2',
  RECORD_OF_ADVICE_COMMERCIAL_REPLACE = 'RECORD_OF_ADVICE_COMMERCIAL_REPLACE',
  RECORD_OF_ADVICE_COMMERCIAL_RENEWAL = 'RECORD_OF_ADVICE_COMMERCIAL_RENEWAL',
  RECORD_OF_ADVICE_LTI = 'RECORD_OF_ADVICE_LTI',
  RECORD_OF_ADVICE_REPLACE_LTI = 'RECORD_OF_ADVICE_REPLACE_LTI',
  GENESIS_PERSONAL_NEW = 'GENESIS_PERSONAL_NEW',
  GENESIS_COMMERCIAL_NEW = 'GENESIS_COMMERCIAL_NEW',
  GENESIS_COMMERCIAL_RENEWAL = 'GENESIS_COMMERCIAL_RENEWAL',
  GENESIS_COMMERCIAL_ENDORSEMENT = 'GENESIS_COMMERCIAL_ENDORSEMENT',
  TEXTBOOK_CHAT = 'TEXTBOOK_CHAT',
  GUIDES = 'GUIDES',
  EMAIL_INGESTION = 'EMAIL_INGESTION',
  LIFE_ONBOARDING = 'LIFE_ONBOARDING',
  INVESTMENT_ONBOARDING_ROA = 'INVESTMENT_ONBOARDING_ROA',
  BROKER_DOC_ASSIST = 'BROKER_DOC_ASSIST',
}

export enum FeatureVisibility {
  GENERAL = 'GENERAL',
  PROPRIETARY = 'PROPRIETARY',
}

export enum FeatureCategory {
  ASSESSMENT = 'ASSESSMENT',
  POLICY = 'POLICY',
  MEDICAL = 'MEDICAL',
  BROKER_PERSONAL = 'BROKER_PERSONAL',
  BROKER_COMMERCIAL = 'BROKER_COMMERCIAL',
  BROKER_LTI = 'BROKER_LTI',
  BROKER_TOOLS = 'BROKER_TOOLS',
  RESOURCES = 'RESOURCES',
}

export interface FeatureDefinition {
  id: Features;
  name: string;
  description: string;
  category: FeatureCategory;
  visibility: FeatureVisibility;
}

export type NotificationType = 'info' | 'success' | 'warning' | 'feature';
export type NotificationStatus = 'pending' | 'active' | 'inactive';

// Multi-button call-to-action (Trello #246 #7). Mirrors the backend
// NotificationCta — `type` drives broker-side routing, `url` is required for the
// URL-driven types (feedback/support/book-demo/link) and ignored for the in-app
// ones (upgrade/buy-tokens).
//
// `open-tool` (feature adoption) is the third shape: its url is a RELATIVE
// in-app path so the client navigates straight into the announced feature
// instead of opening a new tab. The backend refuses to store anything
// scheme-bearing for it.
export type NotificationCtaType = 'upgrade' | 'buy-tokens' | 'feedback' | 'support' | 'book-demo' | 'link' | 'open-tool';
export interface NotificationCta {
  type: NotificationCtaType;
  label: string;
  url?: string;
}

// Real-time admin activity alert (Trello #192) — the notification-centre feed.
// Distinct from `Notification` above (broker-facing notices admins author).
export type AdminAlertType =
  | 'new_user'
  | 'pending_approval'
  | 'domain_cluster'
  | 'new_org'
  // Trial lifecycle (Trello #204) + org usage (Trello #372) — kept in sync with
  // the backend AdminAlertType union (src/types/admin-alert.ts).
  | 'trial_exhausted'
  | 'trial_feedback'
  | 'trial_converted'
  // In-app Feedback & Ideas submission (Trello #371) — the bell ping; the item
  // itself lives in the Feedback page's Ideas tab.
  | 'platform_feedback'
  | 'org_run_limit_reached'
  // Plan change: an org upgraded its subscription tier (e.g. Bronze -> Silver).
  | 'plan_upgraded'
  // Subscription movements (Trello #365) — a downgrade to a cheaper tier, and a
  // cancellation. Both also feed the Subscription Movements tab.
  | 'plan_downgraded'
  | 'subscription_cancelled'
  // Billing reconciliation — the daily backend sweep found a subscription whose
  // recurring debit is overdue, almost always because Stitch attempted it, the
  // card DECLINED, and Stitch sent no webhook at all (Express emits none for a
  // failed recurring debit, so polling its listing is the only signal there is).
  // Always `warning`: this is the only channel that surfaces uncollected
  // revenue. Backend: handlers/subscription/scheduled-billing-reconciliation.ts.
  | 'billing_reconciliation';
export type AdminAlertSeverity = 'info' | 'warning';

export interface AdminAlert {
  id: string;
  stack: AdminStackKey;
  type: AdminAlertType;
  severity: AdminAlertSeverity;
  title: string;
  message: string;
  entityId?: string;
  entityType?: 'user' | 'organization';
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export interface Notification {
  id: string;
  title?: string;
  message: string;
  type: NotificationType;
  link?: string;
  linkText?: string;
  ctas?: NotificationCta[];      // Multi-button CTAs (Trello #246 #7) — supersedes link/linkText
  // Feature-demo media. Both hold an upload-produced PATH
  // (`/notifications/media/<id>.mp4`), NOT a URL — the backend rejects anything
  // else, and the client resolves the path against the API origin.
  videoUrl?: string;
  imageUrl?: string;
  isModal: boolean;
  organizationIds?: string[];
  userIds?: string[];            // Per-user targeting (Trello #246 #7) — Cognito sub ids
  status: NotificationStatus;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  createdBy: string;
  // Set when the notification was CI-published from an MR description block.
  // The admin pending tab surfaces these as a backlink to the MR + Trello card.
  source?: {
    repo?: string;
    mrIid?: number;
    mrUrl?: string;
    trelloUrl?: string;
  };
  dedupKey?: string;
  // ISO timestamp of when this announcement was emailed to its targeted
  // organisations (Trello #395). Present = it has already gone out, so the UI
  // shows the date instead of an armed Send button and the backend refuses a
  // second dispatch without an explicit re-send.
  announcementEmailSentAt?: string;
}

/** What the announcement email would look like, and who it would reach. */
export interface NotificationEmailPreview {
  subject: string;
  /**
   * The WHOLE email document — the announcement body already wrapped in the
   * stack's branded layout, with the logo inlined as a data URI so a browser
   * can draw it (SES resolves the same logo from a `cid:` attachment, which an
   * iframe cannot fetch). Render it as-is; never wrap it in anything here.
   *
   * It carried only the inner body until 2026-08-19, which previewed as an
   * unstyled serif page and read to an admin as though our branding was broken:
   * "the preview looks nothing like the branded emails we normally send."
   */
  bodyHtml: string;
  /**
   * The plain-text copy behind `bodyHtml`, so the dialog's editor seeds from
   * words rather than composed markup. Editing HTML would be unpleasant and
   * would put a second escaping surface in front of a customer's inbox.
   *
   * OPTIONAL because a backend that predates this feature does not return it.
   * The admin app is deployed independently, so that window is real however the
   * deploy order is written down — and a type that promised the field would be
   * lying during it, seeding the editor with `undefined`. The dialog defaults to
   * '' instead, which shows an empty box rather than the string "undefined".
   */
  message?: string;
  organizationIds: string[];
  allOrganizations: boolean;
  alreadySentAt: string | null;
  /**
   * How many distinct people would actually receive this, deduped by address and
   * resolved through the same module the send mails from.
   *
   * The organisation list above is NOT this number. An announcement can target
   * an organisation that contains nobody the send will mail, and until 2026-08-23
   * that case shipped as a success: the dialog reported the announcement as sent,
   * the notification was stamped, and zero emails were handed to SES. The
   * reporter watched an inbox twice for mail that never left (Trello #395).
   *
   * OPTIONAL because a backend that predates this returns no such field, and the
   * admin app deploys independently. `undefined` means "this backend cannot tell
   * you" — which the dialog renders as nothing at all, NOT as zero. Defaulting it
   * to 0 would block sending against every older backend.
   */
  recipientCount?: number;
  /**
   * True when the recipient walk was curtailed by its page safeguard, i.e.
   * `recipientCount` is a FLOOR. Shown as "at least N" rather than presenting a
   * truncated read as an exact audience.
   */
  recipientCountCapped?: boolean;
  /**
   * Whether this preview was resolved for a PAYING-organisations send, echoed
   * back by the server (Trello #395).
   *
   * Doing double duty on purpose. As STATE it tells the dialog which audience
   * the recipient count belongs to. As a CAPABILITY PROBE its absence is the
   * signal that the filter is unavailable — a backend predating #395, or one
   * with the kill-switch off, omits the key entirely, and the dialog then hides
   * the control rather than offering an option the server would ignore.
   *
   * So `undefined` and `false` are NOT interchangeable here: false means "the
   * filter exists and is off", absent means "there is no filter". Same
   * absent-is-not-a-value discipline as `recipientCount` above.
   */
  payingOrganizationsOnly?: boolean;
  /**
   * The organisations this preview was asked to narrow to, echoed back
   * (Trello #395) — the REQUEST, before the server intersected it with the
   * notification's own targeting.
   *
   * Pre-intersection deliberately. The dialog compares its own request against
   * this to decide whether the server has caught up with the audience the admin
   * picked. Comparing against the post-intersection list instead would mismatch
   * on every response whenever the selection held an organisation the
   * notification does not target — re-previewing forever, one user-profiles
   * walk per iteration.
   *
   * Same absent-is-not-a-value discipline as the flag above: ABSENT means this
   * backend does not offer the option and the picker must be hidden; an empty
   * array means it is offered and nothing is selected.
   */
  selectedOrganizationIds?: string[];
  /**
   * Who the send would ACTUALLY reach: the notification's targeting narrowed by
   * any selection above (Trello #395). This is the list the recipient count was
   * resolved under, and what the target chips display.
   *
   * NOT the same as `organizationIds`, which stays the notification's own
   * targeting because that is what the organisation picker draws its options
   * from — narrowing it to the current selection would make an unticked
   * organisation impossible to tick again.
   */
  effectiveOrganizationIds?: string[];
}

export interface FeaturesResponse {
  success: boolean;
  data: {
    features: FeatureDefinition[];
    byCategory: Record<FeatureCategory, FeatureDefinition[]>;
    byVisibility: {
      general: FeatureDefinition[];
      proprietary: FeatureDefinition[];
    };
    categoryLabels: Record<FeatureCategory, string>;
    summary: {
      total: number;
      general: number;
      proprietary: number;
    };
  };
}

/** One organisation's outcome from `POST /admin/features/bulk-update`. */
export interface BulkUpdateFeatureResult {
  organizationId: string;
  success: boolean;
  /** The server's own words, e.g. "Feature X is not available on stack broker-ai". */
  error?: string;
}

/**
 * The bulk-update endpoint answers 200 for a partly-refused run: the top-level
 * `success` is `failed === 0`, and the refusals are per organisation in
 * `results`. Typed here so a caller cannot await the promise and report a win —
 * see `src/views/feature-bulk-update-outcome.ts`.
 */
export interface BulkUpdateFeatureResponse {
  success: boolean;
  /**
   * OPTIONAL on purpose. A backend that predates this shape, a proxy that trimmed
   * the body, or an error path can answer `{ success: false }` with no `data` at
   * all — `summariseBulkUpdate` handles exactly that and is tested for it. Typing
   * it as required would tell the next caller a lie the runtime already disproves.
   */
  data?: {
    featureId: string;
    action: 'enable' | 'disable';
    summary: {
      total: number;
      succeeded: number;
      failed: number;
    };
    results: BulkUpdateFeatureResult[];
  };
}

// A team-account conversion request — a capped user in a shared testing org
// asking for their own billable org (backend org-conversion-requests queue).
export interface ConversionRequest {
  requestId: string;
  userId: string;
  userEmail: string;
  firstName?: string;
  status: 'PENDING' | 'PROVISIONING' | 'PROVISIONED' | 'REJECTED';
  stack: string;
  sourceOrganizationId: string;
  firmName: string;
  accountManagerEmail: string;
  accountManagerName?: string;
  createdAt: string;
  updatedAt: string;
  provisionedOrgId?: string;
}

// EFT payment review queue (broker-AI only, Trello #299). A row is an
// outstanding EFT request: `pending` = invoice issued, no proof yet;
// `awaitingConfirmation` = a proof/bank signal arrived + reconciled, an account
// manager must confirm before the plan activates.
export interface EftPendingRow {
  reference: string;
  organizationId: string;
  tier: string;
  expectedAmountCents: number;
  ocrAmountCents: number | null;
  confidence: string | null;
  proofSource: string | null;
  billingMonth: string;
  createdAt: string;
  status: string;
  invoiceUrl: string | null;
  proofUrl: string | null;
}

// One row on the Subscription Movements tab (broker-AI only, Trello #365) — a
// cancellation, downgrade or upgrade. `source` is 'event' for a recorded
// movement, or 'derived' for a cancellation reconstructed from the subscription
// table (which is how cancellations that predate the movement events still show).
export interface SubscriptionMovementRow {
  id: string;
  organizationId: string;
  organizationName: string;
  movement: 'cancelled' | 'downgraded' | 'upgraded';
  /** The plan they were ON before the movement. */
  fromTier?: string;
  /** The plan they moved TO — absent for a cancellation. */
  toTier?: string;
  /** Cancellation reason the customer gave, when they gave one. */
  reason?: string;
  /** Recurring monthly price before the movement, in ZAR rands. */
  previousMonthlyZAR?: number;
  /** Recurring monthly price after the movement, in ZAR rands (0 for a cancellation). */
  newMonthlyZAR?: number;
  /** Signed change in recurring monthly revenue (rands); absent when either side is unknown. */
  monthlyDeltaZAR?: number;
  occurredAt: string;
  /** When it takes/took effect — period end for a cancel-at-period-end or downgrade. */
  effectiveAt?: string;
  /** True for an operator immediate-cancel; absent on a reconstructed row. */
  immediate?: boolean;
  source: 'event' | 'derived';
}

// A referral source (Trello #336) — a distribution partner an org can be
// acquired through. `code` is the url-safe slug embedded in the partner's
// signup link `https://www.broker-ai.org/?ref=<code>`.
export interface ReferralSource {
  code: string;
  name: string;
  stack: string;
  active: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/** One paying organisation on a partner's monthly commission statement. */
export interface CommissionReportRow {
  organizationId: string;
  name: string;
  /** Bronze / Silver / Gold / Enterprise / Trial / Standard. */
  packageLabel: string;
  /** Subscription / EFT / Managed. */
  paymentMethod: string;
  monthlyZar: number;
  commissionZar: number;
  startedPayingAt: string | null;
}

/**
 * An org referred by the partner that isn't billable this period, with the
 * reason — surfaced rather than dropped so a disputed count can be settled.
 */
export interface CommissionExcludedRow {
  organizationId: string;
  name: string;
  reason: string;
}

/**
 * A partner's monthly commission statement (Trello #362), computed BACKEND-side
 * (`GET /admin/referral-sources/:code/commission-report`). The backend is the
 * single source of truth: this same payload draws the on-screen block AND the
 * Excel / PDF the partner invoices us from, so the two can't disagree.
 */
export interface CommissionReport {
  sourceName: string;
  sourceCode: string;
  period: string;
  periodLabel: string;
  generatedAt: string;
  rows: CommissionReportRow[];
  excluded: CommissionExcludedRow[];
  payingOrgs: number;
  eftOrgs: number;
  totalAttributedOrgs: number;
  commissionableMrrZar: number;
  ratePct: number;
  tierLabel: string;
  commissionZar: number;
}

export type CommissionReportFormat = 'json' | 'xlsx' | 'pdf';

// Admin API functions
export const adminApi = {
  // Users
  listUsers: async (params?: {
    limit?: number;
    paginationToken?: string;
    group?: string;
    includeUsage?: boolean;
    search?: string;
    // Server-side sort across the whole pool. When set, the backend scans every
    // Cognito page, sorts, and returns the full sorted set (no pagination token).
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => {
    const response = await api.get('/admin/users', { params });
    return response.data;
  },

  getUser: async (userId: string) => {
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  },

  getUserJobs: async (userId: string, params?: { 
    limit?: number; 
    startKey?: string;
    status?: string;
    jobType?: string;
  }) => {
    const response = await api.get(`/admin/users/${userId}/jobs`, { params });
    return response.data;
  },

  exportUserJobs: async (userId: string, params: {
    months?: number;
    format?: 'pdf' | 'excel';
  }) => {
    const response = await api.post(`/admin/users/${userId}/export`, params);
    return response.data;
  },

  createUser: async (userData: {
    email: string;
    firstName?: string;
    lastName?: string;
    organizationId?: string;
    isPaid?: boolean;
    maxRunsPerMonth?: number;
    groups?: string[];
    sendInvitation?: boolean;
  }) => {
    const response = await api.post('/admin/users', userData);
    return response.data;
  },

  bulkCreateUsers: async (payload: {
    organizationId: string;
    sendInvitation?: boolean;
    isPaid?: boolean;
    users: Array<{ email: string; firstName?: string; lastName?: string }>;
  }) => {
    const response = await api.post('/admin/users/bulk', payload);
    return response.data;
  },

  getBulkCreateUsersJob: async (jobId: string) => {
    const response = await api.get(`/admin/users/bulk/${jobId}`);
    return response.data;
  },

  disableUser: async (userId: string) => {
    const response = await api.post(`/admin/users/${userId}/disable`);
    return response.data;
  },

  enableUser: async (userId: string) => {
    const response = await api.post(`/admin/users/${userId}/enable`);
    return response.data;
  },

  /**
   * Delete a user. A 2xx does NOT mean both stores were updated — the backend
   * deletes the Cognito account first and cannot roll it back, so a failed
   * user-profiles write still returns success with `profileSyncFailed: true`.
   * That combination is what leaves an orphaned profile row behind (backend
   * Trello #420), so the field is declared rather than left to an untyped read:
   * a caller that ignores it will report a clean delete that did not happen.
   */
  deleteUser: async (userId: string): Promise<DeleteUserResponse> => {
    const response = await api.post(`/admin/users/${userId}/delete`);
    return response.data;
  },

  resendUserInvitation: async (userId: string) => {
    const response = await api.post(`/admin/users/${userId}/resend-invitation`);
    return response.data;
  },

  resendUserVerification: async (userId: string) => {
    const response = await api.post(`/admin/users/${userId}/resend-verification`);
    return response.data;
  },

  resetUserMfa: async (userId: string) => {
    const response = await api.post(`/admin/users/${userId}/reset-mfa`);
    return response.data;
  },

  // Out-of-band onboarding (Trello #239): set a temporary password for an
  // invite-pending user and return it so the admin can convey it directly
  // (e.g. WhatsApp) when the user's email is blocked. Returns { email,
  // temporaryPassword, message }.
  resetUserAccess: async (userId: string) => {
    const response = await api.post(`/admin/users/${userId}/reset-access`);
    return response.data;
  },

  updateUser: async (userId: string, updates: {
    organizationId?: string;
    isPaid?: boolean;
    maxRunsPerMonth?: number;
    maxDocChatLimit?: number;
    maxResearchLimit?: number;
    firstName?: string;
    lastName?: string;
    embeddings?: boolean;
    documentChat?: boolean;
    /**
     * Static tier seat assignment. Broker-AI orgs only. Pass null to clear.
     * The backend snaps the seat's priceZAR + monthlyRunQuota from the
     * catalogue server-side — the UI just sends the chosen tier code.
     */
    tier?: AdminAssignableTier | null;
  }) => {
    const response = await api.put(`/admin/users/${userId}`, updates);
    return response.data;
  },

  // Organizations
  listOrganizations: async () => {
    const response = await api.get('/admin/organizations');
    return response.data;
  },

  getOrganization: async (orgId: string) => {
    const response = await api.get(`/admin/organizations/${orgId}`);
    return response.data;
  },

  createOrganization: async (orgData: {
    name: string;
    displayName?: string;
    domain?: string;
    maxRunsPerMonth?: number;
    sharedRunPool?: boolean;
    features?: string[];
    accountManagerName?: string;
    accountManagerEmail?: string;
    referralSource?: string;
    billingMode?: 'self-serve' | 'managed';
  }) => {
    const response = await api.post('/admin/organizations', orgData);
    return response.data;
  },

  updateOrganization: async (orgId: string, updates: {
    displayName?: string;
    domain?: string;
    maxRunsPerMonth?: number;
    maxDocChatPerMonth?: number;
    maxResearchPerMonth?: number;
    sharedRunPool?: boolean;
    enabledFeatures?: string[];
    status?: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
    accountManagerName?: string;
    accountManagerEmail?: string;
    referralSource?: string;
    // EFT-payer billing marker (Trello #299) — broker-AI UI only; the IA
    // build omits it so a save there can't clear a broker-set marker.
    eftPayer?: boolean;
    // Manually-set IA billing tag (IA admin only via the UI). An explicit
    // null/'' clears it (org shows blank). broker-AI never sends this — its
    // billing state is auto-derived from the org-subscriptions join.
    manualBillingStatus?: 'trial' | 'unpaid' | 'paid' | null;
    // Org owner (Part 2 — payment link). Set together via the owner picker; the
    // denormalised name/email feed the Stitch payer without a Cognito lookup.
    ownerUserId?: string | null;
    ownerName?: string | null;
    ownerEmail?: string | null;
    // NOTE: billingMode is intentionally NOT here — 'managed' is set only via
    // setManagedSubscription (backend update-organization ignores it).
    billingName?: string;
    billingAddress?: string;
    vatNumber?: string;
    costPerLicenseZAR?: number;
    tierName?: string;
    monthlyAmountZAR?: number;
    /**
     * Invoice description line (Trello #670). An empty STRING clears it back to
     * the brand default; `undefined` leaves the stored value untouched — the
     * same convention the document-colour roles above use, and the reason the
     * caller sends `.trim()` rather than `.trim() || undefined`.
     */
    invoiceDescription?: string;
    mfaRequired?: boolean;
    brandingLogoUrl?: string;
    /** LEGACY single font. The backend still reads it as the body face. */
    brandingFont?: string;
    // Document FONT ROLES (Trello #645). An empty string CLEARS that role; an
    // omitted or null role keeps its existing value. There is no sub-heading
    // role — sub-headings take the heading face by design.
    brandingFonts?: {
      heading?: string;
      body?: string;
    };
    // Document colour scheme. `#RRGGBB` or bare 6-hex are both accepted — the
    // backend's update-organization handler validates each role and normalises
    // off the leading `#` before storing. An empty string CLEARS that role
    // (reverting it to the per-template default); an omitted or null role keeps
    // its existing value. One bad value 400s the whole request.
    brandingColors?: {
      heading?: string;
      subheading?: string;
      body?: string;
      highlight?: string;
    };
    // Document LETTERHEAD (Trello #684) — the standing footer block a regulated
    // broker's generated documents carry on every page, plus whether the banner
    // repeats past page one.
    //
    // `footerLines` is REPLACE-not-merge, unlike the colour and font roles above:
    // an address block is edited as a whole, and merging line-by-line would make
    // "remove the third line" impossible to express. Send `null` to clear the
    // letterhead entirely; omit the key to leave it untouched. At most 6 lines of
    // 200 chars — the backend 400s past either.
    brandingLetterhead?: {
      footerLines?: string[];
      repeatHeaderOnEveryPage?: boolean;
    } | null;
  }) => {
    const response = await api.put(`/admin/organizations/${orgId}`, updates);
    return response.data;
  },

  // Activate a "managed" (comp'd) subscription — the org is paid for by us, so it
  // sees no payment page. Writes an ACTIVE no-Stitch subscription at the chosen
  // tier, flips the org to billingMode='managed', and ensures ORG_CREDIT_POOL.
  setManagedSubscription: async (orgId: string, tier: ManagedPlanTier) => {
    const response = await api.post(`/admin/organizations/${orgId}/managed-subscription`, { tier });
    return response.data;
  },

  // Revert a comp'd org back to self-serve — removes the managed (no-charge)
  // subscription and flips billingMode so the org sees the payment page again.
  revertManagedSubscription: async (orgId: string) => {
    const response = await api.delete(`/admin/organizations/${orgId}/managed-subscription`);
    return response.data;
  },

  // Cancel a REAL (Stitch-backed) subscription on the client's behalf. The
  // backend revokes the recurring debit at Stitch first and only then writes the
  // local state, so this can never report "cancelled" while the card is still
  // being charged.
  //   - 'period-end' (default): next debit stops, access runs to the end of the
  //     month they have already paid for.
  //   - 'immediate': access is cut now.
  // NOT for comp'd orgs — those are un-comped with revertManagedSubscription
  // (the backend 409s if you aim this at one).
  cancelSubscription: async (
    orgId: string,
    params: { mode: 'period-end' | 'immediate'; reason?: string },
  ) => {
    const response = await api.post(`/admin/organizations/${orgId}/cancel-subscription`, params);
    return response.data;
  },

  // Referral sources (Trello #336) — admin-managed distribution-partner links.
  // Create a source (server derives its url-safe `code`), list every source in
  // the caller's stack, or soft-deactivate one (its link stops resolving new
  // signups; historical attributions are kept).
  listReferralSources: async () => {
    const response = await api.get('/admin/referral-sources');
    return response.data;
  },

  createReferralSource: async (name: string) => {
    const response = await api.post('/admin/referral-sources', { name });
    return response.data;
  },

  deactivateReferralSource: async (code: string) => {
    const response = await api.delete(`/admin/referral-sources/${encodeURIComponent(code)}`);
    return response.data;
  },

  /**
   * A partner's monthly commission statement (Trello #362). `format: 'json'`
   * returns just the computed report (what the on-screen block renders);
   * 'xlsx' / 'pdf' additionally return the rendered file inline as base64 for
   * `downloadBase64File`.
   */
  getReferralCommissionReport: async (
    code: string,
    period: string,
    format: CommissionReportFormat = 'json',
  ) => {
    const response = await api.get(
      `/admin/referral-sources/${encodeURIComponent(code)}/commission-report`,
      { params: { period, format } },
    );
    return response.data as {
      success: boolean;
      data: {
        report: CommissionReport;
        filename?: string;
        contentType?: string;
        fileBase64?: string;
      };
    };
  },

  // Generate a Stitch recurring-subscription payment link for the org's OWNER —
  // an admin hands it to that person, who pays without logging in. Returns the
  // hosted-page URL. Self-serve orgs only (managed orgs are comp'd, no payment).
  generatePaymentLink: async (orgId: string, tier: 'bronze' | 'silver' | 'gold') => {
    const response = await api.post(`/admin/organizations/${orgId}/payment-link`, { tier });
    return response.data as {
      success: boolean;
      data: { organizationId: string; url: string; subscriptionId: string; tier: string; status: string };
    };
  },

  // Team-account conversion requests (broker-AI). The pending queue + the
  // one-click approve&provision (creates a pooled billable org + moves the user)
  // and reject. See backend org-conversion handlers.
  listConversionRequests: async () => {
    const response = await api.get('/admin/conversion-requests');
    return response.data as {
      success: boolean;
      data: { requests: ConversionRequest[]; total: number };
    };
  },

  approveConversionRequest: async (requestId: string) => {
    const response = await api.post(`/admin/conversion-requests/${requestId}/approve`, {});
    return response.data as {
      success: boolean;
      data: { requestId: string; status: string; provisionedOrgId?: string; alreadyProvisioned?: boolean };
    };
  },

  rejectConversionRequest: async (requestId: string, reason?: string) => {
    const response = await api.post(`/admin/conversion-requests/${requestId}/reject`, { reason });
    return response.data as {
      success: boolean;
      data: { requestId: string; status: string };
    };
  },

  // Subscription movements (broker-AI only, Trello #365) — cancellations,
  // downgrades and upgrades, already merged + newest-first server-side.
  listSubscriptionMovements: async (limit?: number) => {
    const response = await api.get('/admin/subscriptions/movements', {
      params: limit ? { limit } : undefined,
    });
    return response.data as {
      success: boolean;
      data: { movements: SubscriptionMovementRow[]; total: number };
    };
  },

  // EFT payments (broker-AI only, Trello #299). The review queue + the
  // one-click Confirm & Activate (the money->activation seam) and the bank-line
  // record (exact match auto-activates; else holds for review).
  listEftPending: async () => {
    const response = await api.get('/admin/eft/pending');
    return response.data as {
      success: boolean;
      data: { awaitingConfirmation: EftPendingRow[]; pending: EftPendingRow[] };
    };
  },

  confirmEftPayment: async (reference: string, overrideAmountCents?: number) => {
    const response = await api.post('/admin/eft/confirm', {
      reference,
      ...(overrideAmountCents !== undefined ? { overrideAmountCents } : {}),
    });
    return response.data as {
      success: boolean;
      data: {
        reference: string;
        organizationId: string;
        tier: string;
        amountCents: number;
        status: string;
        activated: boolean;
        newlyConfirmed: boolean;
      };
    };
  },

  // Write off an outstanding request (Trello #427): the customer told us they
  // are not taking the subscription this month, so the invoice is void and the
  // monthly reminder cron must stop chasing it. Never valid on a CONFIRMED
  // request — the API answers 409 for that.
  cancelEftRequest: async (reference: string, reason?: string) => {
    const response = await api.post('/admin/eft/cancel', {
      reference,
      ...(reason ? { reason } : {}),
    });
    return response.data as {
      success: boolean;
      data: {
        reference: string;
        organizationId: string;
        billingMonth: string;
        status: string;
        newlyCancelled: boolean;
      };
    };
  },

  recordEftBankPayment: async (
    reference: string,
    amountCents: number,
    opts?: { paidAt?: string; bankReference?: string },
  ) => {
    const response = await api.post('/admin/eft/record-bank-payment', {
      reference,
      amountCents,
      ...(opts?.paidAt ? { paidAt: opts.paidAt } : {}),
      ...(opts?.bankReference ? { bankReference: opts.bankReference } : {}),
    });
    return response.data as {
      success: boolean;
      data: {
        reference: string;
        organizationId: string;
        confidence: string;
        autoConfirmed: boolean;
        activated?: boolean;
        newlyConfirmed?: boolean;
        discrepancies?: string[];
      };
    };
  },

  generateBrandingUploadUrl: async (
    orgId: string,
    params: { contentType: string; fileSize: number }
  ) => {
    const response = await api.post(
      `/admin/organizations/${orgId}/branding/upload-url`,
      params
    );
    return response.data;
  },

  generateEmailBrandingUploadUrl: async (
    orgId: string,
    params: { contentType: string; fileSize: number }
  ) => {
    const response = await api.post(
      `/admin/organizations/${orgId}/branding/email-upload-url`,
      params
    );
    return response.data;
  },

  uploadOrganizationEmailBranding: async (orgId: string, file: File) => {
    // Mirrors uploadOrganizationBranding except we don't persist the key on the
    // org record — the backend HEAD-checks S3 for `email-header.png` directly
    // (see legal-ai-backend src/handlers/admin/get-organization.ts) so an
    // additional updateOrganization call would just add a stale field.
    const presigned = await adminApi.generateEmailBrandingUploadUrl(orgId, {
      contentType: file.type,
      fileSize: file.size,
    });
    if (!presigned?.success) {
      throw new Error(presigned?.message || 'Failed to generate email upload URL');
    }
    const { uploadUrl, key } = presigned.data;

    const putResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });
    if (!putResponse.ok) {
      throw new Error(`S3 upload failed: ${putResponse.status} ${putResponse.statusText}`);
    }

    return { key };
  },

  uploadOrganizationBranding: async (orgId: string, file: File) => {
    // Step 1: get presigned PUT URL from backend
    const presigned = await adminApi.generateBrandingUploadUrl(orgId, {
      contentType: file.type,
      fileSize: file.size,
    });
    if (!presigned?.success) {
      throw new Error(presigned?.message || 'Failed to generate upload URL');
    }
    const { uploadUrl, key } = presigned.data;

    // Step 2: PUT the file directly to S3 (do not use the authed axios instance)
    const putResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });
    if (!putResponse.ok) {
      throw new Error(`S3 upload failed: ${putResponse.status} ${putResponse.statusText}`);
    }

    // Step 3: persist the canonical key on the org record so the UI knows a logo exists
    await adminApi.updateOrganization(orgId, { brandingLogoUrl: key });

    return { key };
  },

  deleteOrganization: async (orgId: string) => {
    const response = await api.delete(`/admin/organizations/${orgId}`);
    return response.data;
  },

  listOrganizationMembers: async (orgId: string, params?: {
    limit?: number;
    lastKey?: string;  // Base64 encoded DynamoDB pagination key
    includeUsage?: boolean;
    // Free-text name/email search across the WHOLE org (Trello #607). The list
    // is paginated, so this must be server-side — a filter over the loaded page
    // would miss members on later pages. Returns all matches, unpaginated.
    search?: string;
  }) => {
    const response = await api.get(`/admin/organizations/${orgId}/members`, { params });
    return response.data;
  },

  // List all runs (jobs) for an organization. Cheap GSI-backed query
  // (OrganizationIndex) — pagination via opaque startKey, newest-first.
  listOrganizationJobs: async (orgId: string, params?: {
    offset?: number;  // 0-based row offset for the requested page
    limit?: number;
    status?: 'PROCESSING' | 'QUEUED' | 'COMPLETED' | 'FAILED';
    jobType?: string;
  }) => {
    const response = await api.get(`/admin/organizations/${encodeURIComponent(orgId)}/jobs`, { params });
    return response.data;
  },

  // Features
  listFeatures: async (): Promise<FeaturesResponse> => {
    const response = await api.get('/admin/features');
    return response.data;
  },

  bulkUpdateFeature: async (params: {
    featureId: string;
    action: 'enable' | 'disable';
    organizationIds: string[] | ['all'];
  }): Promise<BulkUpdateFeatureResponse> => {
    const response = await api.post('/admin/features/bulk-update', params);
    return response.data;
  },

  // Export
  exportUsersExcel: async (params?: {
    period?: string;
    organizationId?: string;
    includeDisabled?: boolean;
  }) => {
    const response = await api.post('/admin/export/users/excel', params);
    return response.data;
  },

  exportUsersPdf: async (params?: {
    period?: string;
    organizationId?: string;
    includeDisabled?: boolean;
    reportTitle?: string;
  }) => {
    const response = await api.post('/admin/export/users/pdf', params);
    return response.data;
  },

  // Dashboard
  getOverviewStats: async (params?: {
    recentJobsOffset?: number;
    recentJobsLimit?: number;
    recentJobsStatus?: 'PROCESSING' | 'QUEUED' | 'COMPLETED' | 'FAILED';
  }): Promise<{ success: boolean; data: OverviewStats }> => {
    const response = await api.get('/admin/stats/overview', { params });
    return response.data;
  },

  getJobDetails: async (jobId: string): Promise<{ success: boolean; data: RecentJob }> => {
    const response = await api.get(`/admin/stats/jobs/${encodeURIComponent(jobId)}`);
    return response.data;
  },

  /** Send a one-shot "we noticed your job failed, looking into it" email to
   * the customer. Backend enforces FAILED-only + once-per-job idempotency
   * (409 on second send). Tenant branding (IAM vs Broker AI) is resolved
   * server-side from the job's organizationId. */
  notifyJobFailure: async (jobId: string): Promise<{ success: boolean; data: { jobId: string; notifiedAt: string } }> => {
    const response = await api.post(`/admin/jobs/${encodeURIComponent(jobId)}/notify-failure`);
    return response.data;
  },

  /** Send a one-shot "good news, your failed job is resolved — please resubmit"
   * email. Only valid AFTER notifyJobFailure (backend 400s otherwise). FAILED-only
   * + once-per-job idempotency (409 on second send). Tenant branding resolved
   * server-side from the job's organizationId. */
  notifyJobResolved: async (jobId: string): Promise<{ success: boolean; data: { jobId: string; resolvedAt: string } }> => {
    const response = await api.post(`/admin/jobs/${encodeURIComponent(jobId)}/notify-resolved`);
    return response.data;
  },

  getCostReport: async (params: { period: string }): Promise<{ success: boolean; data: CostReport }> => {
    const response = await api.get('/admin/stats/cost-report', { params });
    return response.data;
  },

  getProfitability: async (
    params: { months?: number } = {},
  ): Promise<{ success: boolean; data: ProfitabilityReport }> => {
    const response = await api.get('/admin/stats/profitability', { params });
    return response.data;
  },

  // Per-org run-usage health for the selected month (allocated / used / remaining
  // / % consumed + Healthy/Low/Critical/Alert status). Powers the Usage Health page.
  getUsageHealth: async (params: { period: string }): Promise<{ success: boolean; data: UsageHealthReport }> => {
    const response = await api.get('/admin/stats/usage-health', { params });
    return response.data;
  },

  // Per-user run usage for the selected month (runs used + per-user allocation +
  // status, ranked champions-first). Powers the Per-User Usage page.
  getPerUserUsage: async (params: { period: string }): Promise<{ success: boolean; data: PerUserUsageReport }> => {
    const response = await api.get('/admin/stats/per-user-usage', { params });
    return response.data;
  },

  // Most-used tools for the selected month (per-tool runs + share, most-used
  // first). Powers the Most-Used Tools page.
  getMostUsedTools: async (params: { period: string }): Promise<{ success: boolean; data: MostUsedToolsReport }> => {
    const response = await api.get('/admin/stats/most-used-tools', { params });
    return response.data;
  },

  getBrokerAdoption: async (params: { period: string }): Promise<{ success: boolean; data: BrokerAdoptionReport }> => {
    const response = await api.get('/admin/stats/broker-adoption', { params });
    return response.data;
  },

  // Inactive users — bucketed by days since last run (churn risk). "Now"-relative,
  // no period. Powers the Inactive Users page.
  // `search` filters SERVER-side (before the top-N cap), so a match outside the
  // capped page is still found — a client-side filter could not do that (#607).
  getInactiveUsers: async (search?: string): Promise<{ success: boolean; data: InactiveUsersReport }> => {
    const response = await api.get('/admin/stats/inactive-users', {
      params: search ? { search } : undefined,
    });
    return response.data;
  },

  // Inactive users, rendered as a document to send a client (Trello #663). Same
  // route + same computed report as `getInactiveUsers`, so the file and the
  // screen cannot describe different populations — but WITHOUT the top-N cap,
  // because a list a client receives must not be silently short.
  //
  // `threshold` is the tab the admin is looking at; it is applied server-side so
  // the file matches the rows beneath the button.
  exportInactiveUsers: async (
    format: InactiveUsersExportFormat,
    opts: { search?: string; threshold: number },
  ): Promise<{ success: boolean; data: InactiveUsersExport }> => {
    const response = await api.get('/admin/stats/inactive-users', {
      params: {
        format,
        threshold: opts.threshold,
        ...(opts.search ? { search: opts.search } : {}),
      },
    });
    return response.data;
  },

  // Activity leaderboard — most-active users + orgs by runs. Supports a date
  // range (from/to ISO, #514) or a legacy monthly period, plus an optional
  // single-org filter. Powers the Activity Leaderboard page.
  getActivityLeaderboard: async (
    params: { period?: string; from?: string; to?: string; organizationId?: string },
  ): Promise<{ success: boolean; data: ActivityLeaderboardReport }> => {
    const response = await api.get('/admin/stats/activity-leaderboard', { params });
    return response.data;
  },

  getConversionFunnel: async (): Promise<{ success: boolean; data: ConversionFunnelReport }> => {
    const response = await api.get('/admin/stats/conversion-funnel');
    return response.data;
  },

  // Trial feedback — end-of-trial reviews (rating + the two written answers) for
  // the Feedback page. Stack-scoped server-side via the admin's JWT tenant.
  getTrialFeedback: async (): Promise<{ success: boolean; data: TrialFeedbackReport }> => {
    const response = await api.get('/admin/feedback');
    return response.data;
  },

  // Platform feedback (Trello #371) — the in-app Feedback & Ideas queue.
  // Stack-scoped server-side via the admin's JWT tenant, same as above.
  getPlatformFeedback: async (): Promise<{ success: boolean; data: PlatformFeedbackReport }> => {
    const response = await api.get('/admin/platform-feedback');
    return response.data;
  },

  // Move a submission through the triage pipeline. PUT (not PATCH) because the
  // backend's shared CORS allow-methods list doesn't carry PATCH.
  updatePlatformFeedbackStatus: async (
    feedbackId: string,
    status: PlatformFeedbackStatus,
    adminNote?: string,
  ): Promise<{ success: boolean; data: PlatformFeedbackRow }> => {
    const response = await api.put(`/admin/platform-feedback/${feedbackId}/status`, {
      status,
      ...(adminNote !== undefined ? { adminNote } : {}),
    });
    return response.data;
  },

  // AWS Resource Health (admin-only platform metrics — Lambda concurrency, SQS depth/age, DDB throttles, API GW 5xx, etc.)
  // Pass window to control the aggregation period for totals + sparkline. Defaults to 30d if omitted.
  getAwsHealth: async (window?: AwsHealthWindow): Promise<{ success: boolean; data: AwsHealth }> => {
    const response = await api.get('/admin/aws-health', {
      params: window ? { window } : undefined,
    });
    return response.data;
  },

  // Job Status (for async exports)
  getExportJobStatus: async (jobId: string) => {
    const response = await api.get(`/admin/jobs/${jobId}`);
    return response.data;
  },

  // Organization Invoices
  listOrganizationInvoices: async (orgId: string) => {
    const response = await api.get(`/admin/organizations/${orgId}/invoices`);
    return response.data;
  },

  /**
   * Generate the org's invoice PDF for a period.
   *
   * Takes NO override payload (Trello #670). It used to accept a `customData`
   * object — customer name, address, VAT, description, licences, amount — which
   * the backend discarded: `generate-organization-invoice.ts` has read
   * everything from the organisation record since the branded-PDF rewrite and
   * logs any body as `bodyIgnored: true`. Keeping the parameter meant the admin
   * dialog could go on collecting six values that never reached the document,
   * and then fail with a 400 saying those very values were unset.
   *
   * To change what an invoice says, update the org's billing details
   * (`updateOrganization`) — including `invoiceDescription` for the line item.
   */
  generateOrganizationInvoice: async (orgId: string, period: string) => {
    const response = await api.post(
      `/admin/organizations/${orgId}/invoices/${period}/generate`,
      {}
    );
    return response.data;
  },

  // Bulk invoice generation / export across many orgs and a month range
  // (Trello #363). Async — returns a jobId to poll with getExportJobStatus,
  // which resolves to a presigned download (a preview workbook, or a ZIP of the
  // invoices plus that workbook).
  //
  // `preview` commits nothing: no invoice number is allocated and no PDF is
  // written. Use it first on a retroactive range — a back-dated invoice is built
  // from the org's CURRENT billing record and seats.
  bulkGenerateInvoices: async (params: {
    mode: BulkInvoiceMode;
    fromPeriod: string;
    toPeriod: string;
    organizationIds?: string[];
    allOrganizations?: boolean;
  }) => {
    const response = await api.post('/admin/invoices/bulk-generate', params);
    return response.data;
  },

  // Email a generated invoice to the org's account manager. Broker AI orgs are
  // sent from admin@broker-ai.org; others from the default SES source.
  sendOrganizationInvoice: async (orgId: string, period: string) => {
    const response = await api.post(
      `/admin/organizations/${orgId}/invoices/${period}/send`,
      {}
    );
    return response.data;
  },

  // Reset an org's shared credit-pool run counter for a billing period (default
  // current YYYY-MM). Run this once at pool enrollment so pre-existing
  // per-user-summed usage can't instantly cap the org. Backend is admin-only +
  // stack-isolated; only fires for orgs enrolled in the ORG_CREDIT_POOL feature.
  resetOrgPoolCounter: async (orgId: string, period?: string) => {
    const response = await api.post(
      `/admin/organizations/${orgId}/reset-pool-counter`,
      period ? { period } : {}
    );
    return response.data as {
      success?: boolean;
      data?: { organizationId: string; period: string; previousCount: number };
    };
  },

  listOrganizationJobExports: async (orgId: string) => {
    const response = await api.get(`/admin/organizations/${orgId}/job-exports`);
    return response.data;
  },

  // Generate an org job-history export. Pass a month string ('YYYY-MM') for the
  // monthly card, OR a { startDate, endDate } (YYYY-MM-DD) for a custom range
  // (e.g. "last 90 days"). Response.data carries { downloadUrl, jobCount, periodDisplay }.
  exportOrganizationJobs: async (
    orgId: string,
    periodOrRange: string | { startDate: string; endDate: string }
  ) => {
    const body =
      typeof periodOrRange === 'string'
        ? { orgId, period: periodOrRange }
        : { orgId, startDate: periodOrRange.startDate, endDate: periodOrRange.endDate };
    const response = await api.post('/admin/export-organization-jobs', body);
    return response.data;
  },

  // INTERNAL: per-run runs + cost CSV for one org + month. Response.data carries
  // { filename, csvBase64, rowCount, totalCostUsd, totalCostZar }. Pass the live
  // USD->ZAR rate so the CSV's ZAR column matches the dashboard. Gated to the IA
  // admin build in the UI (it exposes cost).
  exportOrganizationRunsCsv: async (orgId: string, period: string, usdZarRate?: number) => {
    const response = await api.post('/admin/export-organization-runs-csv', {
      orgId,
      period,
      usdZarRate,
    });
    return response.data;
  },

  // Real-time admin activity alerts (Trello #192). Seeds the notification-centre
  // history; the live feed arrives over AppSync. Scoped server-side to the caller's
  // admin stack.
  listAdminAlerts: async (limit?: number): Promise<{ alerts: AdminAlert[]; stack: AdminStackKey }> => {
    const response = await api.get(`/admin/alerts${limit ? `?limit=${limit}` : ''}`);
    return response.data?.data ?? { alerts: [], stack: 'intelligent-automation' };
  },

  // Notifications
  listNotifications: async (status?: NotificationStatus | 'all') => {
    const response = await api.get(`/admin/notifications${status ? `?status=${status}` : ''}`);
    return response.data;
  },

  // Flip a pending (CI-published) notification to active so brokers see it.
  publishNotification: async (id: string) => {
    const response = await api.post(`/admin/notifications/${id}/publish`);
    return response.data;
  },

  /**
   * The exact announcement email the send would dispatch (Trello #395).
   * Read-only — nothing is queued and nothing is mutated, so the admin can open
   * this freely before deciding.
   */
  previewNotificationEmail: async (
    id: string,
    /**
     * NAMED options, not positionals (Trello #395).
     *
     * This took two booleans and was about to take a third argument that is
     * neither. Four positionals on the one call that decides who receives
     * unrecallable mail is how a swapped argument ships — `(id, msg, true,
     * false)` reads identically whichever way round the last two go, and
     * nothing catches it. Named fields cannot be transposed.
     */
    options: {
      /**
       * Edited wording to render instead of the notification's own. Sent as a
       * query parameter so this stays a GET — re-previewing while the admin
       * types must not look, or behave, like a write.
       *
       * Query strings are logged verbatim by proxies and CDNs, which is a real
       * consideration and not one here: this is announcement copy an admin is
       * about to mail to every user in an organisation. It is the least private
       * text in the product. If a field with actual privacy weight ever needs
       * the same treatment, it needs a POST, not this.
       */
      message?: string;
      /**
       * Ask the backend to resolve HOW MANY PEOPLE this would reach.
       *
       * Opt-in because the answer does not change as the admin TYPES, while
       * this endpoint is re-requested on a ~500 ms debounce during editing.
       * Counting on every keystroke would re-walk the user-profiles table each
       * time, and for an all-organisations announcement that is a full Scan.
       *
       * It DOES change when the audience changes, so the dialog asks again on a
       * toggle or a selection — not only once when it opens (Trello #395).
       */
      withRecipientCount?: boolean;
      /**
       * Resolve this preview — and its count — for a PAYING-organisations send.
       *
       * The response echoes the value back, and that echo doubles as the
       * dialog's capability probe: a backend predating #395, or one with the
       * kill-switch off, omits the field entirely, and the dialog then hides the
       * control rather than offering an option the server will not apply.
       */
      payingOrganizationsOnly?: boolean;
      /**
       * Narrow this preview to a hand-picked set of organisations (Trello #395).
       *
       * Comma-joined onto the query string. The server intersects it with the
       * notification's own targeting and echoes BOTH lists back — the request as
       * `selectedOrganizationIds` and the resulting audience as
       * `effectiveOrganizationIds` — because the dialog needs to compare
       * against what it ASKED for, not against what the server resolved.
       */
      organizationIds?: string[];
    } = {},
  ): Promise<NotificationEmailPreview> => {
    const { message, withRecipientCount = false, payingOrganizationsOnly = false } = options;
    const organizationIds = options.organizationIds || [];
    const params: Record<string, string> = {};
    if (message !== undefined) params['message'] = message;
    if (withRecipientCount) params['recipients'] = '1';
    if (payingOrganizationsOnly) params['payingOnly'] = '1';
    if (organizationIds.length > 0) params['organizationIds'] = organizationIds.join(',');

    const response = await api.get(`/admin/notifications/${id}/email-preview`, {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
    return response.data?.data;
  },

  /**
   * Email the announcement to every registered user in the notification's
   * targeted organisations. `resend` is required to mail an announcement that
   * has already gone out — the backend 409s without it, because the duplicate
   * this prevents lands in a real customer's inbox.
   *
   * The backend also accepts a `bodyHtml` override, and this type deliberately
   * does NOT expose it: advertising the field here would offer every caller a
   * way to mail arbitrary HTML that the dashboard itself never sends. Edited
   * wording travels as `message` — plain TEXT the server composes with the same
   * function it uses for an untouched draft, so an edit cannot change how the
   * email renders, only what it says.
   *
   * Against a backend that predates the field, `message` is ignored by Joi and
   * the announcement goes out with its ORIGINAL copy. Worth stating rather than
   * leaving to be inferred: this app deploys independently, so admin-ahead-of-
   * backend is a real window, and in it an edit silently does not apply. It
   * never sends something OTHER than what the notification says, which is the
   * direction that matters for mail nobody can recall.
   */
  sendNotificationEmail: async (
    id: string,
    options: {
      subject?: string;
      message?: string;
      resend?: boolean;
      /**
       * Narrow the send to PAYING organisations (Trello #395). Pass `undefined`
       * — not `false` — when the backend has not advertised support: JSON.stringify
       * drops an undefined value, so the key never reaches a server that would
       * reject it as unknown.
       */
      payingOrganizationsOnly?: boolean;
      /**
       * Narrow the send to a hand-picked set of organisations (Trello #395).
       *
       * Same `undefined`-not-`[]` discipline as the flag above: an empty array
       * is a meaningful value the server would try to resolve, so a dialog with
       * nothing selected must omit the key rather than send one.
       */
      organizationIds?: string[];
    } = {},
  ) => {
    // The body is BUILT from named fields, never spread from `options`. A
    // spread makes the exclusion of `bodyHtml` a TypeScript-only promise — a
    // JS caller, or one casting through `any`, would still forward arbitrary
    // email HTML to the backend. Picking the fields makes it structural.
    const response = await api.post(`/admin/notifications/${id}/send-email`, {
      subject: options.subject,
      message: options.message,
      resend: options.resend,
      payingOrganizationsOnly: options.payingOrganizationsOnly,
      organizationIds: options.organizationIds,
    });
    return response.data;
  },

  createNotification: async (data: {
    title?: string;
    message: string;
    type: NotificationType;
    link?: string;
    linkText?: string;
    ctas?: NotificationCta[];
    videoUrl?: string;
    imageUrl?: string;
    isModal?: boolean;
    organizationIds?: string[];
    userIds?: string[];
    expiresAt: string;
  }) => {
    const response = await api.post('/admin/notifications', data);
    return response.data;
  },

  updateNotification: async (id: string, updates: {
    title?: string;
    message?: string;
    type?: NotificationType;
    link?: string;
    linkText?: string;
    ctas?: NotificationCta[];
    videoUrl?: string;
    imageUrl?: string;
    isModal?: boolean;
    organizationIds?: string[];
    userIds?: string[];
    expiresAt?: string;
    status?: NotificationStatus;
  }) => {
    const response = await api.put(`/admin/notifications/${id}`, updates);
    return response.data;
  },

  /**
   * Upload a feature-demo video (or poster image) for a notification.
   *
   * Two hops by design: the backend mints a presigned PUT (which pins the
   * content-type AND the byte length, so the browser cannot deviate from what
   * was authorised) and the file then goes BROWSER → S3 directly, never through
   * the API Gateway — a 100 MB demo clip would blow the Lambda payload limit.
   *
   * Returns the `mediaPath` to store on the notification. The admin never types
   * a URL, which is what keeps videoUrl/imageUrl restricted to our own bucket.
   */
  uploadNotificationMedia: async (
    file: File,
    onProgress?: (percent: number) => void,
  ): Promise<string> => {
    // Mirrors the backend allowlist. The `accept` attribute is only a picker
    // hint (the OS dialog's "All files" defeats it), so without this an admin
    // who picks a .html gets a raw Joi 400 instead of a sentence they can act
    // on. The backend is still the gate — it re-validates and builds the S3 key
    // from a server-side uuid, so the filename never reaches storage.
    const extension = (file.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_MEDIA_EXTENSIONS.includes(extension)) {
      throw new Error(
        `"${file.name}" isn't a supported file. Upload an MP4 or WebM video, or a PNG, JPG or GIF image.`,
      );
    }

    const { data } = await api.post('/admin/notifications/media-upload-url', {
      extension,
      sizeBytes: file.size,
    });
    if (!data?.success || !data?.data?.uploadUrl) {
      // Matches the sibling presign callers; without it a {success:false} body
      // destructures to undefined and surfaces a TypeError instead of the
      // API's own message.
      throw new Error(data?.message || 'Could not start the upload');
    }
    const { uploadUrl, contentType, mediaPath } = data.data;

    // Plain axios (not the `api` instance): the presigned URL carries its own
    // auth in the signature, and sending our Authorization header alongside it
    // makes S3 reject the request.
    await axios.put(uploadUrl, file, {
      headers: { 'Content-Type': contentType },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    });

    return mediaPath as string;
  },

  deleteNotification: async (id: string) => {
    const response = await api.delete(`/admin/notifications/${id}`);
    return response.data;
  },

  // NDAs
  listNdas: async () => {
    const response = await api.get('/admin/ndas');
    return response.data;
  },

  generateNda: async (data: {
    recipientName: string;
    recipientAddress: string;
    startDate: string;
  }) => {
    const response = await api.post('/admin/ndas/generate', data);
    return response.data;
  },

  // Agreements
  listAgreements: async () => {
    const response = await api.get('/admin/agreements');
    return response.data;
  },

  generateAgreement: async (data: {
    customerName: string;
    customerAddress: string;
    effectiveDate: string;
    monthlyFee: string;
    numLicenses: number;
    numRuns: number;
    duration: number;
  }) => {
    const response = await api.post('/admin/agreements/generate', data);
    return response.data;
  },

  // API Keys (Cognito-authed; backend gates privileged scopes on Admins group)
  //
  // Returns TWO lists. `keys` are the org’s minted API keys; `connectors` are
  // the MCP OAuth credentials individual members authorised from an AI client.
  // They were invisible here until 2026-08-30 — which meant an operator could
  // not see, let alone revoke, a credential that reads a broker’s jobs.
  listApiKeys: async (orgId: string) => {
    const response = await api.get(`/organizations/${orgId}/api-keys`);
    const data = response.data as {
      keys: ApiKeySummary[];
      connectors?: McpConnectorSummary[];
    };
    // Defaulted, not assumed: the admin app and the backend deploy separately,
    // so this can run against a backend that predates the field.
    return { ...data, connectors: data.connectors ?? [] };
  },

  createApiKey: async (
    orgId: string,
    data: {
      keyName: string;
      scopes: string[];
      environment: 'production' | 'staging' | 'development';
      rateLimit?: number;
      expiresInDays?: number;
    },
  ) => {
    const response = await api.post(`/organizations/${orgId}/api-keys`, {
      organizationId: orgId,
      ...data,
    });
    return response.data as ApiKeyCreationResponse;
  },

  revokeApiKey: async (orgId: string, keyId: string) => {
    const response = await api.delete(`/organizations/${orgId}/api-keys/${keyId}`);
    return response.data;
  },
};

// API Key types — mirror backend src/types/api-key-types.ts shape we expose to admins
/**
 * An MCP OAuth connector — one member’s AI client (Claude, ChatGPT, …).
 *
 * A separate type from ApiKeySummary because it is a separate thing: a PERSON’s
 * credential rather than an org secret. It has no prefix (its record holds no
 * secret to mask), no create path, and revoking it disconnects a colleague
 * rather than rotating a key — so it gets its own table on the page instead of
 * rows that leave half the columns empty.
 */
export interface McpConnectorSummary {
  keyId: string;
  keyName: string;
  scopes: string[];
  status: 'active' | 'revoked' | 'expired';
  createdAt: string;
  createdBy: string;
  lastUsedAt?: string;
  revokedAt?: string;
  revokedReason?: string;
}

export interface ApiKeySummary {
  keyId: string;
  keyName: string;
  /** Absent on an MCP connector — see McpConnectorSummary. */
  keyPrefix?: string;
  scopes: string[];
  status: 'active' | 'revoked' | 'expired';
  createdAt: string;
  createdBy: string;
  rateLimit: number;
  rateLimitWindow: string;
  expiresAt?: string;
  lastUsedAt?: string;
  currentUsage?: number;
}

export interface ApiKeyCreationResponse {
  keyId: string;
  key: string; // ⚠️ Only shown once
  keyPrefix: string;
  keyName: string;
  scopes: string[];
  createdAt: string;
  rateLimit: number;
  rateLimitWindow: string;
  warning: string;
}

export default api;

